import React, { useState, useRef, useCallback } from "react";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronDown,
  faChevronUp,
  faPhone,
  faShieldAlt,
  faTimes,
  faPaperPlane,
  faChartBar,
  faEdit,
  faCommentDots,
  faExclamationTriangle,
  faCheckCircle,
  faShareAlt,
  faInfoCircle,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import InternationalPhoneInput from "../../utils/InternationalPhoneInputUtil";
import { formatDateTime } from "../../utils/DateTimeUtil";
import {
  getRiskLevelIcon,
  getRiskShortClass,
  isHighRisk,
  isMediumRisk,
  sanitizeHtml,
  decodeAndSanitizeHtml,
} from "./textAnalysisUtils";
import EmptyStatePanel from "../common/EmptyStatePanel";
import "./css/TextMessageAnalysisMobile.css";
import "./css/SmsAnalysisSimpleMobile.css";
import "../../css/Common.css";
import SuggestionsBulletList from "../common/SuggestionsBulletList";
import InlineShareForm from "../common/InlineShareForm";
import useShareAlert from "../common/useShareAlert";
import { useSubscription } from "../../context/SubscriptionContext";
import HelpGuideButton from "../../HelpGuideButton";

// ─────────────────────────────────────────────
// Accordion Section
// ─────────────────────────────────────────────
const AccordionSection = ({
  title,
  icon,
  children,
  defaultOpen = false,
  badge,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`tmsm-accordion ${open ? "tmsm-accordion--open" : ""}`}>
      <button
        type="button"
        className="tmsm-accordion-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="tmsm-accordion-title">
          {icon && (
            <FontAwesomeIcon icon={icon} className="tmsm-accordion-icon" />
          )}
          {title}
          {badge && <span className="tmsm-accordion-badge">{badge}</span>}
        </span>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
      </button>
      {open && <div className="tmsm-accordion-body">{children}</div>}
    </div>
  );
};

// ─────────────────────────────────────────────
// Reusable Detail Sub-Components
// ─────────────────────────────────────────────

const DetailRow = ({ label, children }) => (
  <div className="tmsm-detail-row">
    <span className="tmsm-detail-label">{label}</span>
    {children}
  </div>
);

const DetailBlock = ({ label, children }) => (
  <div className="tmsm-detail-block">
    <span className="tmsm-detail-label">{label}</span>
    {children}
  </div>
);

const RiskChip = ({ riskLevel }) => (
  <span className={`tmsm-risk-chip tmsm-chip-${getRiskShortClass(riskLevel)}`}>
    <FontAwesomeIcon icon={getRiskLevelIcon(riskLevel)} />
    {riskLevel}
  </span>
);

// ─────────────────────────────────────────────
// Phone Analysis Section
// ─────────────────────────────────────────────
const PhoneAnalysisDetails = ({ phoneAnalysis }) => {
  const { t } = useTranslation();
  const [isDatabaseExpanded, setIsDatabaseExpanded] = useState(false);
  if (!phoneAnalysis) return null;

  return (
    <AccordionSection
      title={t("textAnalysis.phone.title")}
      icon={faPhone}
      defaultOpen
    >
      <div className="tmsm-detail-grid">
        {phoneAnalysis.riskLevel && (
          <DetailRow label={t("textAnalysis.phone.riskLevel")}>
            <RiskChip riskLevel={phoneAnalysis.riskLevel} />
          </DetailRow>
        )}
        {phoneAnalysis.summary && (
          <DetailBlock label={t("textAnalysis.phone.summary")}>
            <div
              dangerouslySetInnerHTML={{
                __html: decodeAndSanitizeHtml(phoneAnalysis.summary),
              }}
            />
          </DetailBlock>
        )}
        {phoneAnalysis.description && (
          <DetailBlock label={t("textAnalysis.phone.analysis")}>
            <div
              dangerouslySetInnerHTML={{
                __html: decodeAndSanitizeHtml(phoneAnalysis.description),
              }}
            />
          </DetailBlock>
        )}
        {phoneAnalysis.recommendations?.length > 0 && (
          <DetailBlock label={t("textAnalysis.phone.whatToDo")}>
            <ul className="tmsm-recommendations-list">
              {phoneAnalysis.recommendations.map((rec, i) => (
                <li key={i}>{rec}</li>
              ))}
            </ul>
          </DetailBlock>
        )}
      </div>

      {/* Database Check Results */}
      {phoneAnalysis.sources?.length > 0 && (
        <div className="tmsm-database-section">
          <button
            type="button"
            className="tmsm-database-toggle"
            onClick={() => setIsDatabaseExpanded((prev) => !prev)}
          >
            <span>
              {t("textAnalysis.phone.dbResults").replace(
                "{count}",
                phoneAnalysis.sources.length,
              )}
            </span>
            <FontAwesomeIcon
              icon={isDatabaseExpanded ? faChevronUp : faChevronDown}
            />
          </button>
          {isDatabaseExpanded && (
            <div className="tmsm-database-list">
              {phoneAnalysis.sources.map((source, i) => (
                <div key={i} className="tmsm-source-card">
                  <div className="tmsm-source-header">
                    <FontAwesomeIcon
                      icon={faShieldAlt}
                      className="tmsm-source-icon"
                    />
                    <strong>{source.name}</strong>
                  </div>
                  <div className="tmsm-source-details">
                    <DetailRow label={t("textAnalysis.phone.status")}>
                      <span
                        className={`tmsm-status-chip ${
                          source.found
                            ? "tmsm-status-found"
                            : "tmsm-status-clear"
                        }`}
                      >
                        {source.found
                          ? t("textAnalysis.phone.foundInDb")
                          : t("textAnalysis.phone.notFound")}
                      </span>
                    </DetailRow>
                    {source.reports != null && (
                      <DetailRow label={t("textAnalysis.phone.reports")}>
                        <span>{source.reports}</span>
                      </DetailRow>
                    )}
                    {source.category && (
                      <DetailRow label={t("textAnalysis.phone.category")}>
                        <span>{source.category}</span>
                      </DetailRow>
                    )}
                    {source.lastReported && (
                      <DetailRow label={t("textAnalysis.phone.lastReported")}>
                        <span>{source.lastReported}</span>
                      </DetailRow>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </AccordionSection>
  );
};

// ─────────────────────────────────────────────
// Text/SMS Analysis Section
// ─────────────────────────────────────────────
const TextAnalysisDetails = ({ textAnalysis }) => {
  const { t } = useTranslation();
  if (!textAnalysis) return null;

  return (
    <AccordionSection
      title={t("textAnalysis.textSection.title")}
      icon={faCommentDots}
      defaultOpen
    >
      <div className="tmsm-detail-grid">
        {textAnalysis.riskLevel && (
          <DetailRow label={t("textAnalysis.textSection.riskLevel")}>
            <RiskChip riskLevel={textAnalysis.riskLevel} />
          </DetailRow>
        )}
        {textAnalysis.confidence && (
          <DetailRow label={t("textAnalysis.textSection.confidence")}>
            <span>{textAnalysis.confidence}</span>
          </DetailRow>
        )}
        {textAnalysis.category && (
          <DetailRow label={t("textAnalysis.textSection.category")}>
            <span>{textAnalysis.category}</span>
          </DetailRow>
        )}
        {textAnalysis.scamType &&
          !/^(unknown|other([\s_/]*uncategorized)?)$/i.test(
            textAnalysis.scamType.trim(),
          ) && (
            <DetailRow label={t("textAnalysis.textSection.scamType")}>
              <span className="tmsm-scam-type-chip">
                {textAnalysis.scamType}
              </span>
            </DetailRow>
          )}
        {textAnalysis.score && (
          <DetailRow label={t("textAnalysis.textSection.riskScore")}>
            <span>{textAnalysis.score}</span>
          </DetailRow>
        )}
        {textAnalysis.description && (
          <DetailBlock label={t("textAnalysis.textSection.analysis")}>
            <p>{textAnalysis.description}</p>
          </DetailBlock>
        )}
        {textAnalysis.explanation && (
          <DetailBlock label={t("textAnalysis.textSection.explanation")}>
            <div
              dangerouslySetInnerHTML={{
                __html: decodeAndSanitizeHtml(textAnalysis.explanation),
              }}
            />
          </DetailBlock>
        )}
        {textAnalysis.suggestions && (
          <DetailBlock label={t("textAnalysis.textSection.whatToDo")}>
            <SuggestionsBulletList text={textAnalysis.suggestions} />
          </DetailBlock>
        )}
        {textAnalysis.reasoning && (
          <DetailBlock label={t("textAnalysis.textSection.reasoning")}>
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(textAnalysis.reasoning),
              }}
            />
          </DetailBlock>
        )}
      </div>
    </AccordionSection>
  );
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const TextMessageAnalysisMobile = ({
  phoneNumber,
  textMessage,
  analysisResults,
  error,
  isLoading,
  hasUserInput,
  phoneValidation,
  handlePhoneChange,
  handleTextChange,
  handleClearAnalysis,
  handleSubmit,
  isFormValid,
  shouldShowPhoneValidationError,
  getOverallRiskLevel,
  getOverallRiskType,
  getOverallRecommendation,
  reportId,
  userContext,
  setUserContext,
  phoneInputKey,
}) => {
  const [activeSlide, setActiveSlide] = useState("form");
  const share = useShareAlert();
  const { t } = useTranslation();
  const { canShare } = useSubscription();
  const isIndividualPlan = !canShare();
  const containerRef = useRef(null);

  const scrollTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const showResults = useCallback(() => {
    setActiveSlide("results");
    scrollTop();
  }, [scrollTop]);

  const showForm = useCallback(() => {
    setActiveSlide("form");
    scrollTop();
  }, [scrollTop]);

  // Auto-slide to results when analysis completes
  const prevResultsRef = useRef(analysisResults);
  if (analysisResults && analysisResults !== prevResultsRef.current) {
    prevResultsRef.current = analysisResults;
    if (activeSlide === "form") {
      setTimeout(() => setActiveSlide("results"), 100);
    }
  }

  const overallRisk = getOverallRiskLevel();
  const riskClass = getRiskShortClass(overallRisk);

  return (
    <div className="tmsm-container" ref={containerRef}>
      <div
        className={`tmsm-view-slider ${
          activeSlide === "results" ? "tmsm-slide-results" : ""
        }`}
      >
        {/* ── SLIDE 1: Form Input ── */}
        <div className="tmsm-slide tmsm-slide-form">
          <div className="tmsm-form-wrapper">
            {/* Page Header */}
            <div className="sasm-header">
              <div className="sasm-header-top">
                <h1 className="main-title">{t("textAnalysis.page.title")}</h1>
                <HelpGuideButton guideId={12} />
              </div>
              <p className="common-subtitle">
                {t("textAnalysis.page.subtitle")}
              </p>
            </div>

            {/* Section Header */}
            <div className="tmsm-section-header">
              <FontAwesomeIcon icon={faEdit} className="tmsm-section-icon" />
              <span>{t("textAnalysis.mobile.formSection")}</span>
            </div>

            {/* Error Banner */}
            {error && (
              <div className="tmsm-error-banner">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="tmsm-form">
              {/* Phone Number */}
              <div className="tmsm-form-field">
                <div
                  className={`tmsm-field-label ${
                    isLoading ? "tmsm-disabled" : ""
                  }`}
                >
                  <FontAwesomeIcon icon={faPhone} className="tmsm-field-icon" />
                  <label>{t("textAnalysis.form.phoneLabel")}</label>
                </div>
                <div className="tmsm-phone-input-wrap">
                  <InternationalPhoneInput
                    key={phoneInputKey}
                    id="tmsm-phone-number"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder={t("textAnalysis.form.phonePlaceholder")}
                    disabled={isLoading}
                    className="phone-input compact"
                  />
                </div>
                {shouldShowPhoneValidationError() && (
                  <div className="tmsm-validation-error">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {t("textAnalysis.form.invalidPhone")}
                  </div>
                )}
              </div>

              {/* Message Content */}
              <div className="tmsm-form-field">
                <div className="tmsm-field-label">
                  <FontAwesomeIcon
                    icon={faCommentDots}
                    className="tmsm-field-icon"
                  />
                  <label>{t("textAnalysis.form.messageLabel")}</label>
                </div>
                <textarea
                  value={textMessage}
                  onChange={handleTextChange}
                  placeholder={t("textAnalysis.form.messagePlaceholder")}
                  rows={5}
                  disabled={isLoading}
                  maxLength={1000}
                />
                <div className="tmsm-char-count">
                  {t("textAnalysis.form.charCount").replace(
                    "{count}",
                    textMessage.length,
                  )}
                </div>
              </div>

              {/* Context (optional) */}
              <div className="tmsm-form-field">
                <div className="tmsm-field-label">
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="tmsm-field-icon"
                  />
                  <label>{t("textAnalysis.form.contextLabel")}</label>
                </div>
                <textarea
                  value={userContext}
                  onChange={(e) => setUserContext(e.target.value)}
                  placeholder={t("textAnalysis.form.contextPlaceholder")}
                  rows={3}
                  disabled={isLoading}
                />
              </div>

              {/* Actions */}
              <div className="btn-group">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading || !isFormValid()}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  {isLoading
                    ? t("textAnalysis.form.analyzing")
                    : t("textAnalysis.form.analyzeBtn")}
                </button>

                {hasUserInput && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={handleClearAnalysis}
                    disabled={isLoading}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    {t("textAnalysis.form.clearBtn")}
                  </button>
                )}
              </div>
            </form>

            {/* Quick-jump to results */}
            {analysisResults && (
              <button
                type="button"
                className="btn btn-secondary tmsm-view-results-link"
                onClick={showResults}
              >
                <FontAwesomeIcon icon={faChartBar} />
                {t("textAnalysis.mobile.viewResults")}
              </button>
            )}
          </div>
        </div>

        {/* ── SLIDE 2: Analysis Results ── */}
        <div className="tmsm-slide tmsm-slide-detail">
          <div className="tmsm-results-wrapper">
            {/* Back button */}
            <button type="button" className="tmsm-back-btn" onClick={showForm}>
              <FontAwesomeIcon icon={faArrowLeft} />
              {t("textAnalysis.mobile.backToForm")}
            </button>

            {analysisResults ? (
              <>
                {/* ── Threat Banner (desktop style) ── */}
                <div className="case-details">
                  <div
                    className={`threat-banner tmsm-threat-banner ${isHighRisk(overallRisk) || isMediumRisk(overallRisk) ? "threat-detected" : "safe"}`}
                  >
                    <div className="threat-icon">
                      <FontAwesomeIcon
                        icon={
                          isHighRisk(overallRisk) || isMediumRisk(overallRisk)
                            ? faExclamationTriangle
                            : faCheckCircle
                        }
                      />
                    </div>
                    <h2 className="threat-title">
                      {isHighRisk(overallRisk) || isMediumRisk(overallRisk)
                        ? `${overallRisk || ""} ${t("textAnalysis.mobile.riskDetectedSuffix")}`.trim()
                        : t("textAnalysis.mobile.noThreats")}
                    </h2>
                    {(isHighRisk(overallRisk) || isMediumRisk(overallRisk)) && (
                      <button
                        className="threat-banner-share-btn"
                        onClick={
                          isIndividualPlan
                            ? undefined
                            : (e) => {
                                e.stopPropagation();
                                share.toggleShare();
                              }
                        }
                        disabled={isIndividualPlan}
                        title={
                          isIndividualPlan
                            ? t("textAnalysis.mobile.shareIndividual")
                            : t("textAnalysis.mobile.shareFamily")
                        }
                      >
                        <FontAwesomeIcon
                          icon={faShareAlt}
                          className="threat-share-icon"
                        />
                        <span className="threat-share-label">
                          {t("textAnalysis.mobile.share")}
                        </span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline Share Form */}
                {share.shareOpen &&
                  (isHighRisk(overallRisk) || isMediumRisk(overallRisk)) && (
                    <InlineShareForm
                      shareSuccess={share.shareSuccess}
                      shareError={share.shareError}
                      shareMessage={share.shareMessage}
                      setShareMessage={share.setShareMessage}
                      shareLoading={share.shareLoading}
                      onCancel={share.closeShare}
                      onSend={() => share.sendShare(reportId)}
                    />
                  )}

                {/* ── What Should You Do? (desktop style) ── */}
                {getOverallRecommendation() && (
                  <div className="suggestions-section">
                    <h4 className="subsection-title">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="fa-icon-margin-right-8"
                      />
                      {t("textAnalysis.results.whatToDo")}
                    </h4>
                    <SuggestionsBulletList text={getOverallRecommendation()} />
                  </div>
                )}

                {/* ── Phone Number Analysis ── */}
                <PhoneAnalysisDetails
                  phoneAnalysis={analysisResults.phoneAnalysis}
                />

                {/* ── Text/SMS Analysis ── */}
                <TextAnalysisDetails
                  textAnalysis={analysisResults.textAnalysis}
                />

                {/* ── Analysis Metadata ── */}
                {analysisResults.analysisDate && (
                  <div className="tmsm-metadata">
                    {t("textAnalysis.results.completedOn")}{" "}
                    {formatDateTime(analysisResults.analysisDate)}
                  </div>
                )}

                {/* ── New Scan button ── */}
                <button
                  type="button"
                  className="btn btn-primary tmsm-new-scan-btn"
                  onClick={() => {
                    handleClearAnalysis();
                    showForm();
                  }}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  {t("textAnalysis.mobile.newScan")}
                </button>
              </>
            ) : (
              <div className="tmsm-empty-state">
                <EmptyStatePanel
                  icon={faCommentDots}
                  title={t("textAnalysis.mobile.noResults.title")}
                  description={t("textAnalysis.mobile.noResults.desc")}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={showForm}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  {t("textAnalysis.mobile.goToForm")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextMessageAnalysisMobile;
